
import { useMemo } from "react";
import { Company } from "@/components/CompanyCard";
import { SortField, SortOrder } from "@/components/candidates/CandidatesViewOptions";

export const ITEMS_PER_PAGE = 25;

export function useCompaniesFiltering(
  companies: Company[],
  searchTerm: string,
  sortField: SortField,
  sortOrder: SortOrder,
  currentPage: number
) {
  // Filter and sort companies
  const filteredCompanies = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    // Filter by search term - case insensitive
    const filtered = companies.filter(company => {
      // Search term filter
      const matchesSearch = 
        searchTerm === "" || 
        company.name.toLowerCase().includes(lowerSearchTerm) ||
        (company.address && company.address.toLowerCase().includes(lowerSearchTerm)) ||
        (company.assignee && company.assignee.toLowerCase().includes(lowerSearchTerm));
      
      return matchesSearch;
    });

    // Sort by selected field
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "dateCreated":
          // Using ID as a proxy for creation date since we don't have timestamps
          comparison = a.id.localeCompare(b.id);
          break;
        default:
          comparison = a.name.localeCompare(b.name);
          break;
      }
      
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [
    companies, 
    searchTerm, 
    sortField, 
    sortOrder
  ]);

  // Paginate the filtered companies
  const paginatedCompanies = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredCompanies.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredCompanies, currentPage]);

  const totalPages = Math.ceil(filteredCompanies.length / ITEMS_PER_PAGE);

  return {
    filteredCompanies,
    paginatedCompanies,
    totalPages
  };
}
